import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { showErrorToast, showCustomToast } from '@fff-web/fff-ui-shared/Toast';
import { useRecurly } from '@recurly/react-recurly';
import { datadogLogs } from '@datadog/browser-logs';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import LoadingSpinner from '@fff-web/fff-ui-shared/LoadingSpinner';
import classNames from 'classnames';
import { checkoutReducer, createCheckoutState } from './reducer';
import {
  handleAltPaymentCancel,
  handleRecurlyTokenError,
  handleCaptchaError,
  handleCaptchaExpired,
  handleFieldChange,
  handleVerifyCaptcha,
  setBillingMethod,
  setLoading,
  setPlan,
  setProspectField,
  setTotal,
  handleSubmitError,
  handleAcceptedTerms,
} from './reducer/actionCreators';
import ShippingLocation from './ShippingLocation';
import CreateAccount from './CreateAccount/CreateAccount';
import CreditCardForm from '../Recurly/Forms/CreditCardForm';
import PaymentTabs from '../PaymentTabs/PaymentTabs';
import CountrySelectRecurlyForm from '../CountrySelect/CountrySelect';
import Venmo from '../Payments/Venmo';
import HCaptchaContainer from '../HCaptchaContainer';
import SubmitBtn from '../SubmitBtn/SubmitBtn';
import useTrackField from '../../hooks/useTrackField';
import usePayPal from '../../hooks/usePayPal';
import useApplePay from '../../hooks/useApplePay';
import getProspectFromState from '../../utils/getProspectFromState';
import { handleRegistration } from '../../utils/handleRegistration';
import {
  APPLE_PAY,
  CREDIT_CARD,
  PAYPAL,
  VENMO,
  paymentTypesDictionary,
} from '../../utils/constants';
import { validateForm } from '../../utils/isValidCheckout';
import {
  ICheckoutFormProps,
  IFormChangeEvent,
  TokenPayload,
  RecurlyError,
  IBillingMethods,
  ICustomizeOptions,
  PlanCode,
} from '../../../interfaces';
import styles from './CheckoutForm.module.css';
import { useCreditCardFormState } from '../../hooks';
import { OrderSummaryContainer, GtbOrderSummaryContainer } from '../OrderSummary';
import PaymentIcons from './PaymentIcons';
import { getPlanPriceByPlanCode } from '../OrderSummary/GtbOrderSummary/helpers';
import { usePricesContext } from '../../payments';
import { ShippingCountry } from '../../services/tax';
import TermsAndConditions from '../Shared/TermsAndConditions/TermsAndConditions';
import { createAutoLoginToast, AutoLoginError } from './helpers';

const applePayNotAvailableError = 'Apple Pay is not supported by this device or browser.';

const CheckoutForm: React.FunctionComponent<ICheckoutFormProps> = ({
  flags,
  fieldTracking,
  scrollToForm = () => null,
  showVenmoOption = false,
  customize,
  countries,
  handleExperimentTrackingOnSuccess = () => null,
  orderSummary,
  orderSummaryProps,
  PromoGiftCard,
  CheckoutDisclaimer,
  disclaimerPriceIncrease,
  sku,
  lineItems,
  selectedPlan,
  headerTitle,
  handleBlotoutRegisterTracking = () => null,
  payContext,
  children,
  cartId,
  clickWrapConfig,
}) => {
  const formRef = useRef() as MutableRefObject<HTMLFormElement>;
  const altPaymentStateRef = useRef() as MutableRefObject<any>;
  const captchaRef = useRef<HCaptcha>(null);
  const recurly = useRecurly();
  const [checkoutState, dispatch] = useReducer(checkoutReducer, {}, createCheckoutState);
  const {
    email,
    password,
    shipCountry,
    shipState,
    shipZip,
    total,
    billingMethod,
    planCode,
    promoCode,
    loading,
    shipZipError,
    acceptedTerms,
  } = checkoutState;

  const { prices } = usePricesContext();
  const payFieldsState = useCreditCardFormState(false);
  const { fields: fieldsState } = payFieldsState;
  useTrackField(fieldsState, billingMethod, fieldTracking);
  const isCreditCardPayment = billingMethod === CREDIT_CARD;
  const isUSCountry = shipCountry === 'US';
  const address = {
    shipCountry: shipCountry as ShippingCountry,
    shipState,
    shipZipCode: shipZip,
  };
  const hasRefill = orderSummaryProps?.productData?.hasRefill;
  const prospect = getProspectFromState(checkoutState, {
    firstName: payFieldsState?.fields?.first_name.value as string,
    lastName: payFieldsState?.fields?.last_name.value as string,
  });
  const isFormValid = validateForm(checkoutState, billingMethod);
  const clickWrapConfiguration = useMemo(
    () => ({
      ...clickWrapConfig,
      signerId: email,
      isFormValid,
    }),
    [clickWrapConfig, email, isFormValid]
  );

  const handleFormStateOnChange = useCallback(
    (event: IFormChangeEvent | { target: { name: string; value: any } }) => {
      const { name, value } = event.target as HTMLSelectElement | HTMLInputElement;
      handleFieldChange(dispatch, { name, value });
    },
    []
  );

  const onAltPaymentError = (error: string) => {
    handleRecurlyTokenError(dispatch, error);
    if (error !== applePayNotAvailableError) {
      showErrorToast(error);
    }
  };

  const onAltPaymentCancel = () => {
    handleAltPaymentCancel(dispatch);
  };

  const checkTermsAndShowErrorToast = () => {
    showErrorToast('Checkbox Required', 'Please select the required checkbox to proceed.');
  };

  // trigger proper payment flow according to billing method
  const handlePaymentFlow = () => {
    if (
      orderSummary === 'publicShop' &&
      !acceptedTerms &&
      orderSummaryProps?.productData?.hasIntro
    ) {
      return checkTermsAndShowErrorToast();
    }

    handleSubmitError(dispatch, '');
    handleRecurlyTokenError(dispatch, '');
    setLoading(dispatch, true);

    altPaymentStateRef.current = {
      prospect,
      billingMethod,
    };

    if (billingMethod === APPLE_PAY) {
      handleApplePay();
      return;
    }

    if (billingMethod === PAYPAL) {
      handlePayPal();
      return;
    }

    try {
      getHCaptchaToken()
        .then((hCaptchaToken) => {
          handleVerifyCaptcha(dispatch, hCaptchaToken as string);

          if (billingMethod === CREDIT_CARD) {
            handleCCRecurlyToken(hCaptchaToken as string);
          }
          altPaymentStateRef.current = {
            prospect,
            billingMethod,
            hCaptchaToken,
          };
          if (billingMethod === VENMO) {
            handleVenmo();
          }
        })
        .catch((error) => {
          setLoading(dispatch, false);
          showErrorToast(error);
        });
    } catch (err: any) {
      setLoading(dispatch, false);
      showErrorToast(err);
    }
  };

  const handleOnSubmitError = (error: string | AutoLoginError, autoLogin = false) => {
    const errorMessage = typeof error === 'string' ? error : error.message;
    handleSubmitError(dispatch, errorMessage);

    if (autoLogin) {
      const AutoLoginToastContent = createAutoLoginToast({ error: error as AutoLoginError, email });
      showCustomToast('error', '', AutoLoginToastContent);
    } else {
      showErrorToast(errorMessage);
    }
    scrollToForm();
  };

  const handleApplePay = () => {
    if (window.applePay) {
      window.applePay.begin();
    }
  };

  const handlePayPal = () => {
    if (window.payPal) {
      window.payPal.start();
    }
  };

  const handleVenmo = () => {
    if (window.venmo) {
      window.venmo.start();
    }
  };

  const getHCaptchaToken = () =>
    new Promise((resolve, reject) => {
      const token = captchaRef.current
        ?.execute({ async: true })
        .then(({ response }) => response)
        .catch((error) => {
          datadogLogs.logger.error('HCaptcha token error: ', {}, error);
          reject(error);
          throw new Error(`HCaptcha token error: ${error}`);
        });
      if (!token) throw new Error('Error generating hCaptcha');
      resolve(token);
    });

  const handleCCRecurlyToken = async (captchaToken: string) => {
    recurly.token(
      formRef.current,
      async (err: RecurlyError | null, token: TokenPayload): Promise<any> => {
        if (err) {
          let errorPayload = '';
          if (err.code === 'validation') {
            errorPayload = 'Please fill out the missing fields.';
          } else {
            errorPayload = err.message;
          }
          handleRecurlyTokenError(dispatch, errorPayload);
          datadogLogs.logger.error('CC token error', {}, err);
          // we need to think about this implementation
          scrollToForm();
          showErrorToast(errorPayload);
        }
        if (token && token.id) {
          handleRegistration(
            token.id,
            prospect,
            customize as ICustomizeOptions,
            paymentTypesDictionary[billingMethod],
            payContext,
            countries,
            captchaToken,
            handleOnSubmitError,
            total,
            handleExperimentTrackingOnSuccess,
            handleBlotoutRegisterTracking,
            cartId,
            sku,
            lineItems
          );
        }
      }
    );
  };

  const altPaymentSuccessCallback = async (token: string) => {
    handleRegistration(
      token,
      altPaymentStateRef.current.prospect,
      customize as ICustomizeOptions,
      paymentTypesDictionary[altPaymentStateRef?.current?.billingMethod as IBillingMethods],
      payContext,
      countries,
      altPaymentStateRef.current.hCaptchaToken,
      handleOnSubmitError,
      altPaymentStateRef.current.prospect.total,
      handleExperimentTrackingOnSuccess,
      handleBlotoutRegisterTracking,
      cartId,
      sku,
      lineItems
    );
  };

  const altPaySuccessCallback = async (token: string) => {
    getHCaptchaToken().then((hCaptchaToken) => {
      handleRegistration(
        token,
        altPaymentStateRef.current.prospect,
        customize as ICustomizeOptions,
        paymentTypesDictionary[altPaymentStateRef?.current?.billingMethod as IBillingMethods],
        payContext,
        countries,
        hCaptchaToken as string,
        handleOnSubmitError,
        altPaymentStateRef.current.prospect.total,
        handleExperimentTrackingOnSuccess,
        handleBlotoutRegisterTracking,
        cartId
      );
    });
  };

  useEffect(() => {
    const planTotal = getPlanPriceByPlanCode(prices, planCode);
    setTotal(dispatch, planTotal);
  }, [prices, planCode]);

  // To update plans based on the plans section selection
  useEffect(() => {
    if (selectedPlan) {
      setPlan(dispatch, selectedPlan);
    }
  }, [selectedPlan]);

  const { showApplePayOption } = useApplePay({
    recurly,
    checkoutTotal: total,
    onApplePayVerify: altPaySuccessCallback,
    onApplePayError: onAltPaymentError,
    onApplePayCancel: onAltPaymentCancel,
    flags,
  });

  usePayPal({
    recurly,
    onPaypalVerify: altPaySuccessCallback,
    onPaypalError: onAltPaymentError,
    onPaypalCancel: onAltPaymentCancel,
  });

  return (
    <>
      {loading && <LoadingSpinner />}
      {headerTitle && <h1 className={styles.coTitle}>{headerTitle}</h1>}
      <section className={styles.coBody}>
        <section className={styles.coForm}>
          {children}
          <CreateAccount
            email={email}
            password={password}
            handleOnChange={handleFormStateOnChange}
          />
          <ShippingLocation
            handleFormStateOnChange={handleFormStateOnChange}
            countries={countries}
            shipCountry={shipCountry}
            shipState={shipState}
            shipZip={shipZip}
            shipZipError={shipZipError}
          />
          <section className={styles.pfContainer}>
            <PaymentIcons />
            <PaymentTabs
              billingMethod={billingMethod}
              setBillingMethod={(method) => {
                setBillingMethod(dispatch, method as IBillingMethods);
              }}
              showVenmoOption={isUSCountry && showVenmoOption}
              showApplePayOption={showApplePayOption}
            />
            <form
              ref={formRef}
              className={classNames(styles.paymentForm, {
                [styles.paymentFormContainerHidden]: !isCreditCardPayment,
              })}
            >
              <CreditCardForm
                location="GTB"
                name={fieldsState.first_name}
                lastName={fieldsState.last_name}
                number={fieldsState.number}
                month={fieldsState.month}
                year={fieldsState.year}
                cvv={fieldsState.cvv}
                postalCode={fieldsState.postal_code}
                billingCountry={shipCountry}
              />
              <div className={styles.pfRow} hidden>
                <CountrySelectRecurlyForm
                  fieldState={fieldsState.country}
                  billingCountry={shipCountry}
                />
              </div>
            </form>
          </section>
        </section>
        <section className={styles.coSummary}>
          {orderSummary === 'gtb' && (
            <GtbOrderSummaryContainer
              prospect={prospect}
              setProspect={(value: any) => {
                setProspectField(dispatch, value);
              }}
              customize={customize}
              PromoGiftCard={PromoGiftCard}
              CheckoutDisclaimer={CheckoutDisclaimer}
              disclaimerPriceIncrease={disclaimerPriceIncrease}
            />
          )}
          {orderSummary === 'publicShop' && orderSummaryProps && (
            <OrderSummaryContainer
              {...{
                address,
                planCode: planCode as PlanCode,
                promoCode,
                experienceFlow: 'publicShop',
                setProspect: (value: any) => {
                  setProspectField(dispatch, value);
                },
                flags,
                PromoGiftCard: PromoGiftCard as React.ElementType,
                ...orderSummaryProps,
                cartId,
                acceptedTerms,
                setAcceptedTerms: (checked: boolean) => handleAcceptedTerms(dispatch, checked),
              }}
            />
          )}
          <TermsAndConditions
            clickWrapConfig={clickWrapConfiguration}
            billingMethod={billingMethod}
            isFormValid={isFormValid}
            hasRefill={hasRefill}
          />
          <SubmitBtn
            isDisabled={!isFormValid}
            billingMethod={billingMethod}
            handleSubmit={handlePaymentFlow}
            payContext={payContext}
          />
        </section>
      </section>
      {showVenmoOption && (
        <Venmo
          recurly={recurly as any}
          checkoutTotal={total}
          onVenmoVerify={altPaymentSuccessCallback}
          onVenmoError={onAltPaymentError}
          onVenmoCancel={onAltPaymentCancel}
        />
      )}
      <HCaptchaContainer
        ref={captchaRef}
        onExpire={() => {
          handleCaptchaExpired(dispatch);
        }}
        onError={(err) => {
          handleCaptchaError(dispatch, err);
        }}
      />
    </>
  );
};

export default CheckoutForm;
